import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../constants/api";
import { showError, showForbidden, promise } from "../../utils/toast.js";
import KundenFormular from "./KundenBlockFormular";
import { normalizePhone } from "../../utils/phone.js";
import { useAuth } from "../ui/AuthContext.jsx";
import { useTheme } from "../../context/ThemeContext.jsx";
import { useConfirm } from "../ui/ConfirmationModal.jsx";
import { canEnterHoursUnlimited } from "../../constants/roles.js";
import AppButton from "../ui/AppButton.jsx";
import { invalidateListDomain } from "../../src/hooks/listQueryCache.js";
import { navigateToList } from "../../constants/nav.js";

/* ───────────────── Helpers ───────────────── */
const isObject = v => v && typeof v === "object" && !Array.isArray(v);

// Strings trimmen, Null/Undefined zu "" (nur erste Ebene – genügt hier)
function sanitizePayload(obj) {
  if (!isObject(obj)) return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === "string") out[k] = v.trim();else if (v == null) out[k] = v ?? "";else out[k] = v;
  }
  return out;
}

// flacher Equal-Check für Dirty-Guard (reicht für Formular-Basisfelder)
function shallowEqual(a, b) {
  if (a === b) return true;
  if (!isObject(a) || !isObject(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (a[k] !== b[k]) return false;
  return true;
}
export default function KundenBearbeiten() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
    const {
    user: authUser
  } = useAuth();
  const confirm = useConfirm();
  const [kunde, setKunde] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dupe, setDupe] = useState(null); // 🔍 Duplikat-Hinweis
  const canEditCredit = useMemo(() => canEnterHoursUnlimited(authUser), [authUser]);
  const abortRef = useRef(null);
  const initialRef = useRef(null); // für Dirty-Guard
  const isDirty = useMemo(() => {
    if (!kunde || !initialRef.current) return false;
    return !shallowEqual(sanitizePayload(kunde), sanitizePayload(initialRef.current));
  }, [kunde]);

  // ⚠️ Warnung bei ungespeicherten Änderungen
  useEffect(() => {
    const onBeforeUnload = e => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = ""; // notwendig für einige Browser
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  // Tastatur-Shortcut: Ctrl/Cmd + S => Speichern
  useEffect(() => {
    const onKey = e => {
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        // nur auslösen, wenn wir editierbar sind
        if (!saving && kunde) handleSubmit(new Event("submit"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, kunde]);

  // Laden des Kunden (mit AbortController)
  useEffect(() => {
    if (!id) {
      showError("❌ Ungültige Kunden-ID.");
      navigateToList(navigate, "/kunden");
      return;
    }
    setLoading(true);
    setLoadError(null);
    setDupe(null);

    // laufende Requests abbrechen
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    (async () => {
      try {
        const {
          data
        } = await api.get(`/kunden/${id}`, {
          signal: controller.signal
        });
        setKunde(data || {});
        initialRef.current = data || {};
      } catch (err) {
        if (controller.signal.aborted) return;
        const status = err?.response?.status;
        setLoadError(status || true);
        if (status === 404) showError("❌ Kunde nicht gefunden.");else if (status === 403) showForbidden("default", {
          level: "error"
        });else if (status === 401) showError("🔒 Nicht eingeloggt oder Session abgelaufen.");else showError("❌ Fehler beim Laden des Kunden.");
        navigateToList(navigate, "/kunden");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [id, navigate]);

  // onChange: sowohl DOM-Event als auch Patch-Objekte unterstützen
  const handleChange = useCallback(eOrPatch => {
    setKunde(prev => {
      const base = prev || {};
      if (eOrPatch && eOrPatch.target) {
        const e = eOrPatch;
        const {
          name,
          value
        } = e.target;
        return {
          ...base,
          [name]: value
        };
      }
      if (isObject(eOrPatch)) {
        return {
          ...base,
          ...eOrPatch
        };
      }
      return base;
    });
  }, []);

  // Save-Handler
  const handleSubmit = useCallback(async e => {
    if (e?.preventDefault) e.preventDefault();
    if (!kunde || saving) return;
    try {
      setSaving(true);
      setDupe(null);
      const payload = sanitizePayload(kunde);
      payload.telefon = normalizePhone(payload.telefon);
      payload.telefon2 = normalizePhone(payload.telefon2);
      payload.email = String(payload.email || "").trim().toLowerCase();
      await promise(api.put(`/kunden/${id}`, payload), {
        loading: "Speichere…",
        success: "ok.saved",
        error: "srv.error"
      });

      // Optimistisch initiale Werte aktualisieren (Dirty-Guard zurücksetzen)
      initialRef.current = payload;
      invalidateListDomain("kunden");
      // (Toast kommt aus promise() → success)
      navigate(`/kunden/${id}`);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Fehler beim Speichern";
      if (status === 409) {
        const existingId = err?.response?.data?.existingId;
        const existing = err?.response?.data?.existing;
        setDupe(existingId ? {
          id: existingId,
          ...existing
        } : null);
        // kein Toast – Banner übernimmt Anzeige
      } else if (status === 403) {
        showForbidden("default", {
          level: "error"
        });
      } else if (status === 401) {
        showError("🔒 Session abgelaufen. Bitte neu einloggen.");
      } else {
        showError(`❌ Fehler: ${msg}`);
      }
      console.error("Fehler beim Speichern:", err);
    } finally {
      setSaving(false);
    }
  }, [id, kunde, saving, navigate]);

  // Cancel-Handler mit Dirty-Hinweis
  const handleCancel = useCallback(async () => {
    if (isDirty) {
      const {
        ok
      } = await confirm({
        title: "Änderungen verwerfen?",
        message: "Ungespeicherte Änderungen verwerfen?",
        variant: "warning",
        confirmLabel: "Verwerfen"
      });
      if (!ok) return;
    }
    navigate(`/kunden/${id}`);
  }, [id, isDirty, navigate, confirm]);

  /* ───────────── Render ───────────── */
  const pageCls = "min-h-dvh w-full overscroll-contain bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const cardCls = "glass rounded-2xl border border-white/10 shadow-xl";
  return <div className={pageCls}>
      <div className="mx-auto w-full px-3 sm:px-4 py-4 sm:py-6" style={{
      paddingTop: "max(1rem, env(safe-area-inset-top))",
      paddingBottom: "max(1rem, env(safe-area-inset-bottom))"
    }}>
        <div className="mx-auto max-w-screen-sm md:max-w-3xl">
          <div className={cardCls}>
            <div className="px-4 sm:px-5 py-4 sm:py-5">
              {/* Duplikat-Hinweis */}
              {dupe && <div role="alert" className="mb-4 rounded-xl border border-yellow-400/30 bg-yellow-500/10 text-yellow-200 px-3 py-2 flex items-center justify-between gap-3">
                  <div className="text-sm">
                    <span className="font-medium">
                      Konflikt: Kunde mit diesen Daten existiert bereits:
                    </span>{" "}
                    <span>
                      {dupe.firma ? dupe.firma : [dupe.vorname, dupe.nachname].filter(Boolean).join(" ")}
                      {dupe.ort ? `, ${dupe.ort}` : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AppButton type="button" variant="secondary" onClick={() => navigate(`/kunden/${dupe.id}`)} className="px-3 py-1.5 rounded-lg text-xs bg-yellow-400/20 border border-yellow-300/30 hover:bg-yellow-400/25 transition">
                      Öffnen
                    </AppButton>
                    <AppButton type="button" variant="ghost" onClick={() => setDupe(null)} className={"px-2 py-1.5 rounded-lg text-xs bg-white/10 border border-white/15 hover:bg-white/15 transition"}>
                      Schliessen
                    </AppButton>
                  </div>
                </div>}

              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h1 className={`text-xl font-semibold ${"text-blue-100/90"}`}>
                  🖊 Kunde bearbeiten
                </h1>
                {isDirty && !saving && <span className="text-xs px-2 py-1 rounded bg-yellow-500/10 text-yellow-300 border border-yellow-300/20" aria-live="polite">
                    Änderungen nicht gespeichert
                  </span>}
              </div>

              {/* Content */}
              {loading ? <div role="status" aria-live="polite" className="space-y-4 animate-pulse">
                  <div className={`h-5 w-40 rounded ${"bg-white/10"}`} />
                  <div className={`h-10 w-full rounded ${"bg-white/10"}`} />
                  <div className={`h-10 w-5/6 rounded ${"bg-white/10"}`} />
                  <div className={`h-10 w-3/4 rounded ${"bg-white/10"}`} />
                  <div className={`h-32 w-full rounded ${"bg-white/10"}`} />
                </div> : loadError ? <div className={"text-red-300"}>
                  Fehler beim Laden. Bitte Seite neu öffnen.
                </div> : kunde ? <KundenFormular formData={kunde} onChange={handleChange} onSubmit={handleSubmit} onCancel={handleCancel} isEdit disabled={saving} canEditCredit={canEditCredit} aria-busy={saving ? "true" : "false"} /> : <div className={"text-red-300"}>Keine Kundendaten verfügbar.</div>}
            </div>
          </div>

          <div className="h-4 md:h-6" />
        </div>
      </div>
    </div>;
}